import React, { useState } from "react";
import styles from "./CommercialSolutions.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHotel,
  faUtensils,
  faSchool,
  faBuilding,
  faArrowRight,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";

const CommercialSolutions: React.FC = () => {
  const navigate = useNavigate();
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);

  const solutions = [
    {
      icon: faHotel,
      title: "Hospitality Furniture",
      description:
        "Premium furniture for hotels, resorts, and lounges designed for high traffic and lasting impressions.",
      series: ["Visitor Series", "Bar Stool Series", "Dressing Series"],
      image: "/images/hospitality-bg.jpg",
      gradient:
        "linear-gradient(135deg, rgba(44, 62, 80, 0.9), rgba(52, 73, 94, 0.85))",
    },
    {
      icon: faUtensils,
      title: "Restaurant & Cafe",
      description:
        "Durable and stylish solutions for food service businesses that combine comfort with easy maintenance.",
      series: ["Cafeteria Series", "Bar Stool Series", "Visitor Series"],
      image: "/images/restaurant-bg.jpg",
      gradient:
        "linear-gradient(135deg, rgba(44, 62, 80, 0.9), rgba(52, 73, 94, 0.85))",
    },
    {
      icon: faSchool,
      title: "Educational Institutions",
      description:
        "Ergonomic and robust furniture for schools and universities that withstands daily student use.",
      series: ["Student Series", "Office Table Series", "Visitor Series"],
      image: "/images/education-bg.jpg",
      gradient:
        "linear-gradient(135deg, rgba(44, 62, 80, 0.9), rgba(52, 73, 94, 0.85))",
    },
    {
      icon: faBuilding,
      title: "Corporate Offices",
      description:
        "Professional workspace solutions that enhance productivity and reflect your company's stature.",
      series: ["Director Series", "Executive Series", "Cyber Series"],
      image: "/images/corporate-bg.jpg",
      gradient:
        "linear-gradient(135deg, rgba(44, 62, 80, 0.9), rgba(52, 73, 94, 0.85))",
    },
  ];

  const handleSeriesClick = (seriesName: string) => {
    navigate(`/series/${seriesName.split(" ")[0].toLowerCase()}`);
  };

  return (
    <section
      className={styles.solutionsSection}
      aria-label="Industry-specific solutions"
    >
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Industry-Specific Solutions</h2>
        <div className={styles.titleAccent}></div>
        <p className={styles.sectionSubtitle}>
          Tailored furniture packages for commercial spaces across sectors
        </p>
      </div>

      <div className={styles.solutionsGrid}>
        {solutions.map((solution, index) => (
          <div
            key={index}
            className={styles.solutionCard}
            onMouseEnter={() => setHoveredCard(index)}
            onMouseLeave={() => setHoveredCard(null)}
            style={{
              backgroundImage: `url(${solution.image})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <div
              className={styles.cardOverlay}
              style={{ background: solution.gradient }}
            ></div>

            <div className={styles.cardContent}>
              <div className={styles.cardHeader}>
                <div className={styles.iconContainer}>
                  <FontAwesomeIcon
                    icon={solution.icon}
                    className={styles.icon}
                  />
                </div>
                <h3 className={styles.solutionTitle}>{solution.title}</h3>
              </div>

              <p className={styles.solutionDescription}>
                {solution.description}
              </p>

              <div className={styles.seriesList}>
                <h4 className={styles.seriesTitle}>Recommended Series:</h4>
                <ul className={styles.seriesItems}>
                  {solution.series.map((series, idx) => (
                    <li
                      key={idx}
                      className={styles.seriesItem}
                      onClick={() => handleSeriesClick(series)}
                    >
                      <FontAwesomeIcon
                        icon={faArrowRight}
                        className={styles.seriesArrow}
                      />
                      <span>{series}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <button
                className={styles.exploreButton}
                onClick={() => handleSeriesClick(solution.series[0])}
                aria-label={`Explore ${solution.title} solutions`}
              >
                Explore Solutions
                <FontAwesomeIcon
                  icon={faArrowRight}
                  className={styles.buttonArrow}
                />
              </button>
            </div>

            <div
              className={`${styles.cardHoverEffect} ${
                hoveredCard === index ? styles.active : ""
              }`}
            ></div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default CommercialSolutions;
